import React, { useState, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Modal from '@material-ui/core/Modal';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import SaveIcon from '@material-ui/icons/Save';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import MuiAlert from '@material-ui/lab/Alert';
import SpeedDial from '@material-ui/lab/SpeedDial';
import SpeedDialIcon from '@material-ui/lab/SpeedDialIcon';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';
import { useHistory } from 'react-router-dom';

import BreadcrumbHeader from '../components/BreadcrumbHeader.jsx';
import CardholderAddEdit from '../components/CardholderAddEdit.jsx';
import CardsDevices from '../components/CardsDevices.jsx';
import CardholderDelete from '../components/CardholderDelete.jsx';
import CardholderModal from '../components/CardholderModal.jsx';
import MobileViewPage from '../components/MobileViewPage.jsx';
import ReportsCardholderActivity from '../components/ReportsCardholderActivity.jsx';
import { getOffsetDate, dateIsAfter } from '../util/dateHelpers.js';
import {
  GET_CARDHOLDER,
  DELETE_MOBILE_CREDENTIAL,
  RESEND_MOBILE_CREDENTIAL_TEXT,
} from '../util/schema';

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const sleep = (milliseconds) => {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
};

const GET_FIELD_CONFIG = gql`
  query cardholderFieldConfig {
    cardholderFieldConfig {
      fieldRows {
        weight
        fields {
          id
          label
          weight
          type
          required
          selectedMapField
          mapOptions {
            id
            data
          }
        }
      }
    }
  }
`;

const GET_ACCESS_GROUPS = gql`
  query accessGroups($first: Int!) {
    accessGroups(first: $first) {
      edges {
        id
        name
      }
    }
  }
`;

const GET_READERS = gql`
  query singleReaderAccessRights($first: Int!) {
    readers(first: $first) {
      engageReaders {
        id
        name
        type
      }
    }
  }
`;

const UPDATE_CARDHOLDER = gql`
  mutation updateCardholder($cardholderId: Int!, $input: CardholderInput!) {
    updateCardholder(cardholderId: $cardholderId, input: $input) {
      id
      firstName
      middleInitial
      lastName
      employeeNumber
      employeeId
      title
      address
      building
      room
      city
      province
      postalCode
      country
      company
      office
      assistant
      homePhone
      fax
      workPhone
      emailAddress
      pager
      mobilePhone
      dateOfBirth
      sex
      height
      weight
      hairColor
      eyeColor
      startDate
      endDate
      ssn
      credentials
      preferredName
      notes
      lostBadgeCount
      homeroomNumber
      schoolBusNumber
      schoolIdNumber
      grade
      emergencyContact
      contactPhoneNumber
      migrationId
      active
      photo
      cards {
        active
        cardNumber
        primary
        accessGroups {
          id
          name
        }
        singleReaderAccessRights {
          engageReaders {
            id
            name
            type
          }
        }
        issueCode
        activationDate
        antipassbackExempt
        engageCardFormat
        siteCode
        engageIssueCode
        cardFunction
        deactivationDate
        freeAntipassback
        vacationStartDate
        vacationEndDate
        issueCode
        pin
        useAdaTiming
        useLimit
      }
      mobileCredentials {
        id
        active
        useAdaTiming
        activationDate
        deactivationDate
        cardFunction
        accessGroups {
          id
          name
        }
        singleReaderAccessRights {
          engageReaders {
            id
            name
            type
          }
        }
      }
      customFields {
        key
        value
      }
    }
  }
`;

const CREATE_CARDHOLDER = gql`
  mutation createCardholder($input: CardholderInput!) {
    createCardholder(input: $input) {
      id
      firstName
      middleInitial
      lastName
      employeeNumber
      employeeId
      title
      address
      building
      room
      city
      province
      postalCode
      country
      company
      office
      assistant
      homePhone
      fax
      workPhone
      emailAddress
      pager
      mobilePhone
      dateOfBirth
      sex
      height
      weight
      hairColor
      eyeColor
      startDate
      endDate
      ssn
      credentials
      preferredName
      notes
      lostBadgeCount
      homeroomNumber
      schoolBusNumber
      schoolIdNumber
      grade
      emergencyContact
      contactPhoneNumber
      migrationId
      active
      photo
      customFields {
        key
        value
      }
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  paper: {
    height: '100%',
    overflowY: 'auto',
    position: 'relative',
    width: '100%',
  },
  content: {
    marginTop: theme.spacing(1),
  },
  cardholderRecord: {
    display: 'flex',
  },
  half: {
    width: '50%',
    display: 'inline-block',
    verticalAlign: 'top',
    minHeight: '700px',
  },
  firstHalf: {
    borderRight: '1px solid gainsboro',
  },
  secondHalf: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1.2),
    width: '48.5%',
  },
  btn: {
    margin: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  actionWrapper: {
    display: 'flex',
    justifyContent: 'end',
    flexDirection: 'column',
    width: '50%',
    marginLeft: '50%',
    position: 'relative',
  },
  dirtyWarning: {
    marginRight: theme.spacing(2),
    textAlign: 'end',
  },
  mobilePage: {
    width: '100%',
    display: 'inline-block',
    verticalAlign: 'top',
    minHeight: '425px',
  },
  credentialsMobile: {
    padding: theme.spacing(3),
  },
  root: {
    flexGrow: 1,
  },
  speedDial: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(2),
    zIndex: 10000,
  },
  toolTip: {
    width: '100%',
  },
}));

const CardholderEditPage = ({ match, isMobile }) => {
  const isNewRecord = match.params.id === 'new';
  const classes = useStyles();
  const [cardholder, setCardholder] = useState({});
  const [fieldConfig, setFieldConfig] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [cardholderToDelete, setCardholderToDelete] = useState({});
  const [contactFieldConfig, setContactFieldConfig] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [accessGroups, setAccessGroups] = useState([]);
  const [singleReaderAccessRights, setSingleReaderAccessRights] = useState([]);
  const [opensnack, setOpenSnack] = useState(false);
  const [openerrorsnack, setErrorSnack] = useState(false);
  const [credentialSnack, setCredentialSnack] = useState(false);
  const history = useHistory();
  const [actionMsg, setActionMsg] = useState('added');
  const [credentialMsg, setCredentialMsg] = useState('add');
  const [contentType, setContentType] = useState('Add Card');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cardData, setCardData] = useState();
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState();
  //const [logoFile, setLogoFile] = useState(`${window.location.origin}/logo192.png`); // Default to pdc logo
  const [reportTimeFrame, setReportTimeFrame] = useState('last24Hrs');
  // TODO: fetch report data from API once it is implemented
  const now = new Date();
  const MS_PER_MINUTE = 60000;
  const MS_PER_DAY = 86400000;
  const allCardholderReportData = [
    // TODO: Remove this placeholder data?
    {
      id: 'ch1',
      door: 'N.E. Main Entrance',
      result: 'Access Granted',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 10 * MS_PER_MINUTE).toISOString(),
      type: 'granted-card',
    },
    {
      id: 'ch2',
      door: 'N.E. Main Entrance',
      result: 'Access Denied',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 11 * MS_PER_MINUTE).toISOString(),
      type: 'denied-card',
    },
    {
      id: 'ch3',
      door: 'N.E. Main Entrance',
      result: 'Access Denied',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 12 * MS_PER_MINUTE).toISOString(),
      type: 'denied-card',
    },
    {
      id: 'ch4',
      name: 'New Mobile Credential Activated',
      credential: "Angel's Phone",
      credentialType: 'mobile',
      timestamp: new Date(now - 20 * MS_PER_MINUTE).toISOString(),
      type: 'new-mobile',
    },
    {
      id: 'ch5',
      door: 'N.E. Main Entrance',
      result: 'Access Denied',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 1 * MS_PER_DAY).toISOString(),
      type: 'denied-card',
    },
    {
      id: 'ch6',
      name: 'New Mobile Credential Activated',
      credential: "Angel's Phone",
      credentialType: 'mobile',
      timestamp: new Date(now - 2 * MS_PER_DAY).toISOString(),
      type: 'new-mobile',
    },
    {
      id: 'ch8',
      door: 'N.E. Main Entrance',
      result: 'Access Granted',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 3 * MS_PER_DAY).toISOString(),
      type: 'granted-card',
    },
    {
      id: 'ch9',
      door: 'N.E. Main Entrance',
      result: 'Access Granted',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 7 * MS_PER_DAY).toISOString(),
      type: 'granted-card',
    },
    {
      id: 'ch10',
      door: 'N.E. Main Entrance',
      result: 'Access Granted',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 10 * MS_PER_DAY).toISOString(),
      type: 'granted-card',
    },
    {
      id: 'ch10',
      door: 'N.E. Main Entrance',
      result: 'Access Granted',
      credential: '1234',
      credentialType: 'card',
      timestamp: new Date(now - 20 * MS_PER_DAY).toISOString(),
      type: 'granted-card',
    },
  ];
  const [cardholderReportData, setCardholderReportData] = useState(allCardholderReportData);

  const getFilteredCardholderActivity = () => {
    const allowedTypes = ['granted-card', 'denied-card', 'denied-mobile', 'granted-mobile'];
    if (reportTimeFrame === 'all') {
      return allCardholderReportData;
    }
    const offsetDate = getOffsetDate(reportTimeFrame);
    const filteredData = cardholderReportData.filter((record) => {
      return dateIsAfter(record.timestamp, offsetDate) && allowedTypes.includes(record.type);
    });
    return filteredData;
  };
  const handleReportTimeframeChange = (value) => {
    setReportTimeFrame(value);
    const offsetDate = getOffsetDate(reportTimeFrame);
    const filteredData = cardholderReportData.filter((record) => {
      return dateIsAfter(record.timestamp, offsetDate);
    });
    setCardholderReportData(filteredData);
  };

  let countAdd = 0;

  const updateCountAdd = (e) => {
    countAdd += 1;
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleFABClose = () => {
    setOpen(false);
  };

  const handleShowPopper = async (cardsData, selectIndex, event) => {
    setCardData(cardsData);
    setSelectedIndex(selectIndex);
    updateCountAdd();
    setContentType('Edit Card');
  };

  useEffect(() => {
    if (contentType === 'Edit Card') {
      countAdd = 0;
      sleep(500).then(() => {
        setMobileOpen(true);
      });
    }
  }, [contentType]);
  const [userPrompt, setuserPrompt] = useState(false);
  const [openModal, setopenModal] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);

  useEffect(() => {
    const path = history.location.pathname;
    const unblock = history.block((location, action) => {
      if (path !== location.path && userPrompt) {
        setopenModal(true);
        return false;
      }
      // TODO: Need to return something in this branch
    });

    return () => {
      unblock();
    };
  }, [userPrompt]);

  /**
   * Helper function to set the cardholder values, and flag the form as 'dirty'
   * @param {Object} data cardholder data to save
   */
  const handleSetCardholder = (data) => {
    setIsBtnDisabled(false);
    setCardholder(data);
    setIsDirty(true);
    setuserPrompt(true);
  };

  const queryCompleted = (data) => {
    setCardholder(data && data.cardholder);
  };

  const queryAGCompleted = (data) => {
    setAccessGroups(data.accessGroups.edges);
  };

  const queryReadersCompleted = (data) => {
    setSingleReaderAccessRights(data.readers.engageReaders);
  };

  /**
   * Helper function to set field configuration objects based on the data returned from the config query
   * TODO: set field group state for each group of fields defined in the object.
   * @param {*} data Query result data
   */
  const queryConfigCompleted = (data) => {
    setFieldConfig(data.cardholderFieldConfig.fieldRows);
    setContactFieldConfig(data.cardholderFieldConfig.contactRows);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnack(false);
    setErrorSnack(false);
    setCredentialSnack(false);
  };

  const [newRecordError, setNewRecordError] = useState(false);
  const queryError = ({ graphQLErrors, networkError }) => {
    console.log('Error GQL: ', graphQLErrors);
    console.log('Error Network: ', networkError);
    setOpenSnack(false);
    setErrorSnack(true);
    setNewRecordError(true);
  };

  /**
   * Helper function to convert cardholder data into a data format that the API expects.
   * TODO: update this to include more fields as they are implemented in the API.
   * @param {*} chCards
   */
  const cardsFromCardholder = (chCards) => {
    if (!chCards) {
      return [];
    }
    return chCards.map((card) => {
      const accessGroups = card.accessGroups
        ? card.accessGroups.map((ag) => {
            return { id: parseInt(ag.id) };
          })
        : [];
      let readers = [];
      if (card.singleReaderAccessRights.engageReaders) {
        readers = card.singleReaderAccessRights.engageReaders
          ? card.singleReaderAccessRights.engageReaders.map((reader) => {
              return { id: reader.id, type: reader.type };
            })
          : [];
      }
      const formattedReaders = {
        engageReaders: readers,
      };
      return {
        activationDate: card.activationDate,
        active: card.active,
        antipassbackExempt: card.antipassbackExempt,
        engageCardFormat: card.engageCardFormat,
        siteCode: parseInt(card.siteCode),
        engageIssueCode: parseInt(card.engageIssueCode),
        cardFunction: card.cardFunction,
        deactivationDate: card.deactivationDate,
        // description: card.description, // TODO: uncomment when enabling description field
        doNotUpdateUseCount: card.doNotUpdateUseCount,
        freeAntipassback: card.freeAntipassback,
        issueCode: parseInt(card.issueCode),
        pin: card.pin,
        primary: card.primary,
        singleReaderAccessRights: formattedReaders,
        useAdaTiming: card.useAdaTiming,
        useLimit: parseInt(card.useLimit),
        cardNumber: parseInt(card.cardNumber),
        accessGroups,
        vacationStartDate: card.vacationStartDate,
        vacationEndDate: card.vacationEndDate,
      };
    });
  };

  /**
   * Helper function to convert cardholder data into a data format that the API expects.
   * TODO: update this to include more fields as they are implemented in the API.
   * @param {*} chCredentials
   */
  const credentialsFromCardholder = (chCredentials) => {
    if (!chCredentials) {
      return [];
    }
    return chCredentials.map((credential) => {
      const accessGroups = credential.accessGroups
        ? credential.accessGroups.map((ag) => {
            return { id: parseInt(ag.id) };
          })
        : [];
      let readers = [];
      if (credential.singleReaderAccessRights.engageReaders) {
        readers = credential.singleReaderAccessRights.engageReaders
          ? credential.singleReaderAccessRights.engageReaders.map((reader) => {
              return { id: reader.id, type: reader.type };
            })
          : [];
      }
      const formattedReaders = {
        engageReaders: readers,
      };
      return {
        id: parseInt(credential.id),
        activationDate: credential.activationDate,
        active: credential.active,
        useAdaTiming: credential.useAdaTiming,
        cardFunction: credential.cardFunction,
        deactivationDate: credential.deactivationDate,
        singleReaderAccessRights: formattedReaders,
        accessGroups,
      };
    });
  };

  const inputFromCardholder = (ch) => {
    const formattedCards = cardsFromCardholder(ch.cards);
    const formattedCredentials = credentialsFromCardholder(ch.mobileCredentials);
    if (ch.customFields) {
      const formattedCustom = ch.customFields.map((field) => {
        return { key: field.key, value: field.value };
      });
      const obj = {
        firstName: ch.firstName,
        middleInitial: ch.middleInitial,
        lastName: ch.lastName,
        employeeNumber: ch.employeeNumber,
        employeeId: ch.employeeId,
        title: ch.title,
        address: ch.address,
        building: ch.building,
        room: ch.room,
        city: ch.city,
        province: ch.province,
        postalCode: ch.postalCode,
        country: ch.country,
        company: ch.company,
        office: ch.office,
        assistant: ch.assistant,
        homePhone: ch.homePhone,
        fax: ch.fax,
        workPhone: ch.workPhone,
        emailAddress: ch.emailAddress,
        pager: ch.pager,
        mobilePhone: ch.mobilePhone,
        dateOfBirth: ch.dateOfBirth,
        sex: ch.sex,
        height: ch.height,
        weight: ch.weight,
        hairColor: ch.hairColor,
        eyeColor: ch.eyeColor,
        startDate: ch.startDate,
        endDate: ch.endDate,
        ssn: ch.ssn,
        credentials: ch.credentials,
        preferredName: ch.preferredName,
        notes: ch.notes,
        lostBadgeCount: ch.lostBadgeCount,
        homeroomNumber: ch.homeroomNumber,
        schoolBusNumber: ch.schoolBusNumber,
        schoolIdNumber: ch.schoolIdNumber,
        grade: ch.grade,
        emergencyContact: ch.emergencyContact,
        contactPhoneNumber: ch.contactPhoneNumber,
        migrationId: ch.migrationId,
        active: ch.active,
        photo: ch.photo,
        cards: formattedCards,
        mobileCredentials: formattedCredentials,
        customFields: formattedCustom,
      };
      return obj;
    }
    return {
      firstName: ch.firstName,
      lastName: ch.lastName,
      title: ch.title,
      employeeNumber: ch.employeeNumber,
      workPhone: ch.workPhone,
      mobilePhone: ch.mobilePhone,
      address: ch.address,
      city: ch.city,
      province: ch.province,
      postalCode: ch.postalCode,
      photo: ch.photo,
      office: ch.office,
      cards: formattedCards,
      customFields: ch.customFields,
    };
  };

  const { data, loading, error } = useQuery(GET_CARDHOLDER, {
    variables: { cardholderId: parseInt(match.params.id) },
    notifyOnNetworkStatusChange: true,
  });

  // This is a bit of a hack to account for Apollo cache updates not triggering 'onCompleted'
  // in @apollo/client versions < 3.5. Upgrading to 3.5+ breaks Jest tests in this project
  // See: https://github.com/apollographql/apollo-client/issues/9191
  useEffect(() => {
    if (error) {
      queryError(error);
    } else if (data) {
      queryCompleted(data);
    }
  }, [data, error, loading]);

  const { loading: loadingConfig, ERROR: errorConfig } = useQuery(GET_FIELD_CONFIG, {
    notifyOnNetworkStatusChange: true,
    onCompleted: queryConfigCompleted,
    onError: queryError,
    fetchPolicy: 'no-cache',
  });

  const { loading: loadingAccessGroups, ERROR: errorAccessGroups } = useQuery(GET_ACCESS_GROUPS, {
    variables: { first: 1000 },
    notifyOnNetworkStatusChange: true,
    onCompleted: queryAGCompleted,
    onError: queryError,
  });

  const { loading: loadingSingleReader, ERROR: errorSingleReader } = useQuery(GET_READERS, {
    variables: { first: 1000 },
    notifyOnNetworkStatusChange: true,
    onCompleted: queryReadersCompleted,
    onError: queryError,
  });

  const handleClose = () => {
    setShowDeleteDialog(false);
  };

  const handleModalClose = () => {
    setuserPrompt(false);
    setopenModal(false);
  };

  const handleDelete = ({ cardholder }) => {
    setCardholderToDelete(cardholder);
    setShowDeleteDialog(true);
  };

  const handleCardholderSave = (event) => {
    event.hasOwnProperty('updateCardholder') ? setActionMsg('updated') : setActionMsg('added');
    setOpenSnack(true);
    setErrorSnack(false);
    setuserPrompt(false);
  };

  const handleCredentialDeleteSuccess = () => {
    setActionMsg('mobile credential deleted');
    setOpenSnack(true);
    setErrorSnack(false);
    setCredentialSnack(false);
    setuserPrompt(false);
  };

  const handleCredentialSendTextSuccess = () => {
    setActionMsg('mobile credential text sent');
    setOpenSnack(true);
    setErrorSnack(false);
    setCredentialSnack(false);
    setuserPrompt(false);
  };

  const handleCredentialDeleteFailure = () => {
    setCredentialMsg('delete');
    setOpenSnack(false);
    setCredentialSnack(true);
  };

  const handleMobileCredentialSuccess = () => {
    setActionMsg('mobile credential added');
    setOpenSnack(true);
    setErrorSnack(false);
    setCredentialSnack(false);
    setuserPrompt(false);
  };

  const handleMobileCrentialFailure = () => {
    setActionMsg('add');
    setOpenSnack(false);
    setCredentialSnack(true);
  };

  // TODO: call updateCardholder when submitting cardholder edit form
  // eslint-disable-next-line no-unused-vars
  const [updateCardholder] = useMutation(UPDATE_CARDHOLDER, {
    variables: { cardholderId: cardholder.id, input: inputFromCardholder(cardholder) },
    onError: queryError,
    onCompleted: handleCardholderSave,
  });

  const [deleteMobileCredential] = useMutation(DELETE_MOBILE_CREDENTIAL, {
    onError: handleCredentialDeleteFailure,
    onCompleted: handleCredentialDeleteSuccess,
    // eslint-disable-next-line no-shadow
    update(cache, { data: { deleteMobileCredential } }) {
      const existing = cache.readQuery({
        query: GET_CARDHOLDER,
        variables: { cardholderId: cardholder.id },
      });

      const newCardholder = { ...existing.cardholder };
      newCardholder.mobileCredentials = newCardholder.mobileCredentials.filter(
        (c) => c.id !== deleteMobileCredential.id
      );

      if (newCardholder.mobileCredentials.length === 0) {
        newCardholder.mobileCredentials = null;
      }

      cache.writeQuery({
        query: GET_CARDHOLDER,
        variables: { cardholderId: cardholder.id },
        data: { cardholder: { ...newCardholder } },
      });
    },
  });

  const [resendMobileCredential] = useMutation(RESEND_MOBILE_CREDENTIAL_TEXT, {
    onError: queryError,
    onCompleted: handleCredentialSendTextSuccess,
  });

  const [createCardholder] = useMutation(CREATE_CARDHOLDER, {
    variables: { input: inputFromCardholder(cardholder) },
    onError: queryError,
    onCompleted: handleCardholderSave,
  });

  const breadcrumbList = [
    { name: 'Cardholders', to: '/cardholders' },
    { name: isNewRecord ? 'New Cardholder' : 'Edit Cardholder' },
  ];

  const handleApply = () => {
    setIsBtnDisabled(true);
    if (isNewRecord) {
      createCardholder();
    } else {
      updateCardholder();
    }
    setIsDirty(false);
  };
  const actionButtons = (
    <div className={classes.actionWrapper}>
      {isDirty && (
        <div className={classes.dirtyWarning}>
          <Typography color="error"> Unsaved Changes! </Typography>
        </div>
      )}
      <div>
        <span style={{ float: 'right', marginRight: '10px' }}>
          <Button
            className={classes.btn}
            onClick={() => {
              setIsDirty(false);
              window.history.back();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className={classes.btn}
            color="secondary"
            disabled={isBtnDisabled}
            onClick={handleApply}
          >
            Apply
          </Button>
          <Button
            variant="contained"
            className={classes.btn}
            type="submit"
            color="secondary"
            disabled={isBtnDisabled}
          >
            Save
          </Button>
        </span>
        <span style={{ float: 'left' }}>
          <Button
            className={classes.btn}
            onClick={() => handleDelete({ cardholder })}
            style={{ color: '#f44336' }}
          >
            Delete
          </Button>
        </span>
      </div>
    </div>
  );
  let fieldsWithValues = [];
  let contactFieldsWithValues = [];
  const formatFieldGroup = (config) => {
    return config.map((row) => {
      const populatedRow = row.fields.map((field) => {
        if (isNewRecord && !cardholder.customFields) {
          cardholder.customFields = [];
        }
        const changeHandler = (e) => {
          setIsBtnDisabled(false);
          const updatedCardholder = { ...cardholder };
          if (field.type === 'mapping' || field.id.startsWith('CardholderExt')) {
            const fieldIndex = updatedCardholder.customFields.findIndex((f) => f.key === field.id);
            if (fieldIndex !== -1) {
              const newFields = updatedCardholder.customFields.map((f) => ({
                key: f.key,
                value: f.value,
              }));
              newFields[fieldIndex].value =
                e.target.value === '' ? null : e.target.value.toString();
              updatedCardholder.customFields = newFields;
            } else if (isNewRecord) {
              updatedCardholder.customFields.push({
                key: field.id,
                value: e.target.value.toString(),
              });
            }
          } else {
            updatedCardholder[field.id] =
              field.type === 'integer' ? parseInt(e.target.value) : e.target.value;
          }
          handleSetCardholder(updatedCardholder);
        };
        return {
          value:
            (field.type === 'mapping' || field.id.startsWith('CardholderExt')) &&
            cardholder.customFields
              ? cardholder.customFields.find((f) => f.key === field.id)?.value
              : cardholder[field.id] || '',
          changeHandler,
          ...field,
        };
      });
      return { weight: row.weight, fields: populatedRow };
    });
  };
  if (fieldConfig) {
    fieldsWithValues = formatFieldGroup(fieldConfig);
  }
  if (contactFieldConfig) {
    contactFieldsWithValues = formatFieldGroup(contactFieldConfig);
  }

  /**
   * update cards array
   * @param {*} card Card object
   * @param {*} index index of card object to update
   */
  const updateCard = (card, index) => {
    const cards = [...cardholder.cards];
    const updatedCardholder = { ...cardholder };
    if (cards) {
      cards[index] = card;
      updatedCardholder.cards = cards;
    } else {
      updatedCardholder.cards = [card];
    }
    handleSetCardholder(updatedCardholder);
  };

  /**
   * update mobile credentials array
   * @param {*} credential Mobile Credential object
   * @param {*} index index of mobile credential object to update
   */
  const updateMobileCredential = (credential, index) => {
    const mobileCredentials = [...cardholder.mobileCredentials];
    const updatedCardholder = { ...cardholder };
    if (mobileCredentials) {
      mobileCredentials[index] = credential;
      updatedCardholder.mobileCredentials = mobileCredentials;
    } else {
      updatedCardholder.mobileCredentials = [credential];
    }
    handleSetCardholder(updatedCardholder);
  };

  const changePrimaryCard = (newPrimaryIndex) => {
    const updatedCardholder = { ...cardholder };
    const updatedCards = [];
    if (cardholder.cards) {
      cardholder.cards.forEach((card, index) => {
        const mutableCard = { ...card };
        if (index === newPrimaryIndex) {
          mutableCard.primary = true;
        } else if (card.primary) {
          mutableCard.primary = false;
        }
        updatedCards.push(mutableCard);
      });
      updatedCardholder.cards = updatedCards;
      handleSetCardholder(updatedCardholder);
    }
  };

  const updateCardMobile = async (card) => {
    const cards = [...cardholder.cards];
    const updatedCardholder = { ...cardholder };
    if (cards) {
      cards[selectedIndex] = card;
      updatedCardholder.cards = cards;
    } else {
      updatedCardholder.cards = [card];
    }
    handleSetCardholder(updatedCardholder);
  };

  /**
   * add an object for the new card to the cards array
   * @param {*} cardValues field values of the new card record
   */
  const addCard = (cardValues) => {
    const updatedCardholder = { ...cardholder };
    let cards = cardholder.cards ? [...cardholder.cards] : [];
    if (cards) {
      cards.push(cardValues);
    } else cards = [cardValues];
    updatedCardholder.cards = cards;
    handleSetCardholder(updatedCardholder);
  };

  /**
   * remove a card from the cards array
   * @param {*} cardIndex index of the card object to remove
   */
  const deleteCard = (cardIndex) => {
    const updatedCardholder = { ...cardholder };
    const mutableCards = updatedCardholder.cards.map((card) => card); // cardholder.cards is readonly. Create a mutable array to manipulate
    if (mutableCards) {
      mutableCards.splice(cardIndex, 1);
    }
    updatedCardholder.cards = mutableCards;
    handleSetCardholder(updatedCardholder);
  };

  /**
   * Remove one or more cards by index.
   * @param {number[]} cardIndeces
   */
  const deleteCards = (cardIndeces) => {
    const updatedCardholder = { ...cardholder };
    const mutableCards = updatedCardholder.cards.filter((card, index) => {
      return !cardIndeces.includes(index);
    });
    updatedCardholder.cards = mutableCards;
    handleSetCardholder(updatedCardholder);
  };

  /**
   * Remove a mobile credentials by index.
   * @param {number} credentialIndex
   */
  const deleteCredential = (credentialIndex) => {
    const updatedCardholder = { ...cardholder };
    const credential = updatedCardholder.mobileCredentials[credentialIndex];
    deleteMobileCredential({ variables: { id: credential.id } });
  };

  /**
   * Resend a mobile credential text
   * @param {number} credentialIndex
   */
  const resendCredential = (credentialIndex) => {
    const updatedCardholder = { ...cardholder };
    const credential = updatedCardholder.mobileCredentials[credentialIndex];
    resendMobileCredential({ variables: { id: credential.id } });
  };

  const getCardsPage = () => {
    document.location.href = `/cardholders`;
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    handleApply();
    if (isMobile) {
      sleep(1500).then(() => {
        window.location.reload();
      });
    } else {
      sleep(1500).then(() => {
        if (window.history.state) {
          document.location.href = `/cardholders`;
        } else {
          window.history.back();
        }
      });
    }
  };
  const generateRendering = () => {
    if (isNewRecord) {
      if (isMobile) {
        return (
          <div className={classes.content}>
            <form
              data-testid="cardholderForm"
              className={classes.container}
              onSubmit={handleSubmit}
            >
              <Typography variant="h6" style={{ padding: '8px' }}>
                Add Cardholder
              </Typography>
              <MobileViewPage
                contentType={contentType}
                cardData={cardData}
                mobileOpen={mobileOpen}
                setContentType={setContentType}
                setMobileOpen={setMobileOpen}
                setCardData={setCardData}
                addCard={addCard}
                selectedIndex={selectedIndex}
                deleteCard={deleteCard}
                updateCard={updateCardMobile}
              />
              <div className={classes.cardholderRecord}>
                <Paper className={classes.paper} square>
                  <div className={`${classes.mobilePage} `}>
                    <CardholderAddEdit
                      cardholder={cardholder}
                      setCardholder={handleSetCardholder}
                      dataEntryFields={fieldsWithValues}
                      contactFields={contactFieldsWithValues}
                      isMobile={isMobile}
                    />
                  </div>
                  <br />
                  <br />
                  <Button
                    variant="contained"
                    className={classes.btn}
                    style={{
                      backgroundColor: '#5ba9e1',
                      color: '#fff',
                      display: 'grid',
                      margin: 'auto',
                    }}
                    onClick={() => {
                      updateCountAdd();
                      setContentType('Add Card');
                      setCardData(null);
                      sleep(100).then(() => {
                        setMobileOpen(true);
                      });
                    }}
                  >
                    Tap to add Card
                  </Button>
                  <div className={`${classes.mobilePage} ${classes.credentialsMobile} `}>
                    <CardsDevices
                      cardholder={cardholder}
                      updateCard={updateCard}
                      updateMobileCredential={updateMobileCredential}
                      addCard={addCard}
                      accessGroups={accessGroups}
                      readers={singleReaderAccessRights}
                      singleReaderAccessRights={singleReaderAccessRights}
                      changePrimaryCard={changePrimaryCard}
                      deleteCards={deleteCards}
                      deleteCredential={deleteCredential}
                      resendCredential={resendCredential}
                      isMobile={isMobile}
                      showPopper={handleShowPopper}
                      mobileCredentialSuccess={handleMobileCredentialSuccess}
                      mobileCredentialFailure={handleMobileCrentialFailure}
                    />
                    {isDirty && (
                      <div className={classes.dirtyWarning}>
                        <Typography style={{ position: 'relative', float: 'left' }} color="error">
                          Unsaved Changes!
                        </Typography>
                      </div>
                    )}
                  </div>
                </Paper>
              </div>
            </form>
          </div>
        );
      }
      return (
        <div className={classes.content}>
          <form data-testid="cardholderForm" className={classes.container} onSubmit={handleSubmit}>
            {loading && <CardholderAddEdit isNewRecord={isNewRecord} cardholder={{}} />}
            {!loading && (
              <>
                <div className={classes.cardholderRecord}>
                  <Paper className={classes.paper} square>
                    <div className={`${classes.half} ${classes.firstHalf}`}>
                      <CardholderAddEdit
                        cardholder={cardholder}
                        setCardholder={handleSetCardholder}
                        dataEntryFields={fieldsWithValues}
                        contactFields={contactFieldsWithValues}
                        isMobile={isMobile}
                        isNewRecord={isNewRecord}
                      />
                    </div>
                    {newRecordError ? (
                      <div className={`${classes.half} ${classes.secondHalf}`}>
                        <CardsDevices
                          cardholder={cardholder}
                          updateCard={updateCard}
                          updateMobileCredential={updateMobileCredential}
                          addCard={addCard}
                          accessGroups={accessGroups}
                          readers={singleReaderAccessRights}
                          singleReaderAccessRights={singleReaderAccessRights}
                          changePrimaryCard={changePrimaryCard}
                          deleteCards={deleteCards}
                          deleteCredential={deleteCredential}
                          resendCredential={resendCredential}
                          isMobile={isMobile}
                          mobileCredentialSuccess={handleMobileCredentialSuccess}
                          mobileCredentialFailure={handleMobileCrentialFailure}
                        />
                      </div>
                    ) : null}
                  </Paper>
                </div>
                {actionButtons}
              </>
            )}
          </form>
        </div>
      );
    }
    if (isMobile) {
      return (
        <div className={classes.content}>
          <form data-testid="cardholderForm" className={classes.container} onSubmit={handleSubmit}>
            <Typography variant="h6" style={{ padding: '8px' }}>
              Edit Record
            </Typography>
            <MobileViewPage
              contentType={contentType}
              cardData={cardData}
              mobileOpen={mobileOpen}
              setContentType={setContentType}
              setMobileOpen={setMobileOpen}
              setCardData={setCardData}
              addCard={addCard}
              selectedIndex={selectedIndex}
              deleteCard={deleteCard}
              updateCard={updateCardMobile}
            />
            <div className={classes.cardholderRecord}>
              <Paper className={classes.paper} square>
                <div className={`${classes.mobilePage} `}>
                  <CardholderAddEdit
                    cardholder={cardholder}
                    setCardholder={handleSetCardholder}
                    dataEntryFields={fieldsWithValues}
                    contactFields={contactFieldsWithValues}
                    isMobile={isMobile}
                  />
                </div>
                <br />

                <br />
                <Button
                  variant="contained"
                  className={classes.btn}
                  style={{
                    backgroundColor: '#5ba9e1',
                    color: '#fff',
                    display: 'grid',
                    margin: 'auto',
                  }}
                  onClick={() => {
                    updateCountAdd();
                    setContentType('Add Card');
                    setCardData(null);
                    sleep(100).then(() => {
                      setMobileOpen(true);
                    });
                  }}
                >
                  Tap to add Card
                </Button>
                <div className={`${classes.mobilePage} ${classes.credentialsMobile} `}>
                  <CardsDevices
                    cardholder={cardholder}
                    updateCard={updateCard}
                    updateMobileCredential={updateMobileCredential}
                    addCard={addCard}
                    accessGroups={accessGroups}
                    readers={singleReaderAccessRights}
                    singleReaderAccessRights={singleReaderAccessRights}
                    changePrimaryCard={changePrimaryCard}
                    deleteCards={deleteCards}
                    deleteCredential={deleteCredential}
                    resendCredential={resendCredential}
                    isMobile={isMobile}
                    showPopper={handleShowPopper}
                    mobileCredentialSuccess={handleMobileCredentialSuccess}
                    mobileCredentialFailure={handleMobileCrentialFailure}
                  />
                  {isDirty && (
                    <div className={classes.dirtyWarning}>
                      <Typography style={{ position: 'relative', float: 'left' }} color="error">
                        Unsaved Changes!
                      </Typography>
                    </div>
                  )}
                </div>
              </Paper>
            </div>
          </form>
          <ReportsCardholderActivity
            data={getFilteredCardholderActivity(reportTimeFrame)}
            // logo={logoFile}
            timeSelector={reportTimeFrame}
            updateTimeSelector={handleReportTimeframeChange}
            cardholderName={`${cardholder.lastName}-${cardholder.firstName}`}
          />
        </div>
      );
    }
    return (
      <div className={classes.content}>
        <form data-testid="cardholderForm" className={classes.container} onSubmit={handleSubmit}>
          {loading && <CardholderAddEdit cardholder={{}} />}
          {!loading && !error && !loadingConfig && !errorConfig && (
            <>
              <div className={classes.cardholderRecord}>
                <Paper className={classes.paper} square>
                  <div className={`${classes.half} ${classes.firstHalf}`}>
                    <CardholderAddEdit
                      cardholder={cardholder}
                      setCardholder={handleSetCardholder}
                      dataEntryFields={fieldsWithValues}
                      contactFields={contactFieldsWithValues}
                      isMobile={isMobile}
                    />
                  </div>
                  <div className={`${classes.half} ${classes.secondHalf}`}>
                    <CardsDevices
                      cardholder={cardholder}
                      updateCard={updateCard}
                      updateMobileCredential={updateMobileCredential}
                      addCard={addCard}
                      cardholderId={parseInt(match.params.id)}
                      deleteCard={deleteCard}
                      accessGroups={accessGroups}
                      readers={singleReaderAccessRights}
                      singleReaderAccessRights={singleReaderAccessRights}
                      changePrimaryCard={changePrimaryCard}
                      deleteCards={deleteCards}
                      deleteCredential={deleteCredential}
                      resendCredential={resendCredential}
                      mobileCredentialSuccess={handleMobileCredentialSuccess}
                      mobileCredentialFailure={handleMobileCrentialFailure}
                    />
                    <ReportsCardholderActivity
                      data={getFilteredCardholderActivity(reportTimeFrame)}
                      // logo={logoFile}
                      timeSelector={reportTimeFrame}
                      id={cardholder.id}
                      updateTimeSelector={handleReportTimeframeChange}
                      cardholderName={`${cardholder.lastName}-${cardholder.firstName}`}
                    />
                  </div>
                  {actionButtons}
                </Paper>
              </div>
            </>
          )}
        </form>
      </div>
    );
  };
  // TODO: add translations for breadcrumb
  return (
    <>
      <div style={isMobile ? { display: 'none' } : {}}>
        <BreadcrumbHeader
          title="Cardholder Record"
          breadcrumbList={breadcrumbList}
          userPrompt={userPrompt}
          setopenModal={setopenModal}
        >
          <Grid item xs={12} md={4}>
            {/* <CardholderSearch /> */}
          </Grid>
        </BreadcrumbHeader>
      </div>

      <div className={classes.root} style={!isMobile || mobileOpen ? { display: 'none' } : {}}>
        <SpeedDial
          ariaLabel="SpeedDial openIcon example"
          className={classes.speedDial}
          icon={<SpeedDialIcon />}
          onClose={handleFABClose}
          onOpen={handleOpen}
          open={open}
        >
          <SpeedDialAction
            tooltipTitle="Save"
            key="Save"
            tooltipOpen
            icon={<SaveIcon />}
            onClick={handleSubmit}
          />
          <SpeedDialAction
            tooltipTitle="Apply"
            key="Apply"
            tooltipOpen
            icon={<SaveOutlinedIcon />}
            onClick={handleApply}
          />
          <SpeedDialAction
            style={isNewRecord ? { display: 'none' } : {}}
            tooltipTitle="Add Cardholder"
            className={classes.toolTip}
            TooltipClasses={classes.toolTip}
            key="Add Cardholder"
            tooltipOpen
            icon={<AddCircleOutlineIcon />}
          />
        </SpeedDial>
      </div>

      {generateRendering()}
      <Snackbar open={opensnack} autoHideDuration={3000} onClose={handleSnackClose}>
        <Alert onClose={handleSnackClose} severity="success">
          {`Cardholder ${actionMsg} successfully`}
        </Alert>
      </Snackbar>
      {/* TODO: handle snackbar error after page reloading api call issue got fixed */}
      {/* <Snackbar open={openerrorsnack} autoHideDuration={3000} onClose={handleSnackClose}>
        <Alert severity="error" onClose={handleSnackClose} open={openerrorsnack}>
          Cardholder failed to add
        </Alert>
      </Snackbar> */}
      <Snackbar open={credentialSnack} autoHideDuration={3000} onClose={handleSnackClose}>
        <Alert severity="error" onClose={handleSnackClose} open={credentialSnack}>
          {`Failed to ${credentialMsg} mobile credential`}
        </Alert>
      </Snackbar>
      <Modal open={showDeleteDialog} onClose={handleClose}>
        <div>
          <CardholderDelete
            cardholder={cardholderToDelete}
            onClose={handleClose}
            onDelete={getCardsPage}
          />
        </div>
      </Modal>
      <Modal open={openModal} onClose={handleModalClose}>
        <div>
          <CardholderModal updateCardholder={updateCardholder} cardholder={cardholderToDelete} />
        </div>
      </Modal>
    </>
  );
};

export default CardholderEditPage;
